import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { ArrowRight, ExternalLink } from 'lucide-react';
export function WalletsAPIPage() {
  const createTransferCode = {
    curl: `curl -X POST https://api.obseq.com/v1/wallets/transfers \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "source_wallet_id": "wa_123",
    "destination_wallet_id": "wa_456",
    "amount": 100000,
    "currency": "USD",
    "description": "Payment for services"
  }'`,
    node: `const transfer = await fetch('https://api.obseq.com/v1/wallets/transfers', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID()
  },
  body: JSON.stringify({
    source_wallet_id: 'wa_123',
    destination_wallet_id: 'wa_456',
    amount: 100000,
    currency: 'USD',
    description: 'Payment for services'
  })
});`,
    python: `transfer = requests.post(
    'https://api.obseq.com/v1/wallets/transfers',
    headers={
        'Authorization': f'Bearer {api_key}',
        'Idempotency-Key': str(uuid.uuid4())
    },
    json={
        'source_wallet_id': 'wa_123',
        'destination_wallet_id': 'wa_456',
        'amount': 100000,
        'currency': 'USD',
        'description': 'Payment for services'
    }
)`,
    go: `transfer := map[string]interface{}{
    "source_wallet_id": "wa_123",
    "destination_wallet_id": "wa_456",
    "amount": 100000,
    "currency": "USD",
}

body, _ := json.Marshal(transfer)
req, _ := http.NewRequest("POST", "https://api.obseq.com/v1/wallets/transfers", bytes.NewBuffer(body))`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'API Reference',
    href: '/docs/api-reference'
  }, {
    label: 'Wallets',
    href: '/docs/api-reference/wallets'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'Resources',
    id: 'resources'
  }, {
    title: 'Endpoints',
    id: 'endpoints'
  }, {
    title: 'Create Transfer',
    id: 'create-transfer'
  }, {
    title: 'Webhooks',
    id: 'webhooks'
  }, {
    title: 'Errors',
    id: 'errors'
  }]} data-id="element-2033">
      <h1 data-id="element-2034">Wallets API</h1>
      <p className="lead" data-id="element-2035">
        Wallets provide multi-currency balances, optional sub-accounts, and a
        ledger for reconciliation and audit.
      </p>

      <div className="flex gap-3 my-6" data-id="element-2036">
        <a href="#/docs/guides/getting-started" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2037">
          Try in Sandbox
          <ExternalLink className="w-4 h-4" data-id="element-2038" />
        </a>
      </div>

      <h2 id="overview" data-id="element-2039">Overview</h2>
      <p data-id="element-2040">
        Wallets hold balances across multiple currencies and provide a complete
        ledger for tracking all balance changes. Use wallets for holding funds,
        internal transfers, and reconciliation.
      </p>

      <h2 id="resources" data-id="element-2041">Key Resources</h2>
      <table className="w-full text-sm my-6" data-id="element-2042">
        <thead data-id="element-2043">
          <tr className="border-b border-slate-200" data-id="element-2044">
            <th className="text-left py-2 pr-4" data-id="element-2045">Resource</th>
            <th className="text-left py-2 pr-4" data-id="element-2046">Object</th>
            <th className="text-left py-2" data-id="element-2047">Status Values</th>
          </tr>
        </thead>
        <tbody data-id="element-2048">
          <tr className="border-b border-slate-100" data-id="element-2049">
            <td className="py-3 pr-4" data-id="element-2050">
              <code data-id="element-2051">wallet</code>
            </td>
            <td className="py-3 pr-4" data-id="element-2052">
              <code data-id="element-2053">wallets.wallet</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-2054">active, frozen, closed</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2055">
            <td className="py-3 pr-4" data-id="element-2056">
              <code data-id="element-2057">transfer</code>
            </td>
            <td className="py-3 pr-4" data-id="element-2058">
              <code data-id="element-2059">wallets.transfer</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-2060">
              created, pending, succeeded, failed
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2061">
            <td className="py-3 pr-4" data-id="element-2062">
              <code data-id="element-2063">ledger_entry</code>
            </td>
            <td className="py-3 pr-4" data-id="element-2064">
              <code data-id="element-2065">wallets.ledger_entry</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-2066">N/A (immutable)</td>
          </tr>
        </tbody>
      </table>

      <h3 data-id="element-2067">Status Lifecycle</h3>
      <div className="my-6 bg-slate-50 border border-slate-200 rounded-lg p-6" data-id="element-2068">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-2069">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-2070">
            <span className="text-sm font-semibold text-blue-900" data-id="element-2071">created</span>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 rotate-90 md:rotate-0" data-id="element-2072" />
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-2073">
            <span className="text-sm font-semibold text-yellow-900" data-id="element-2074">
              pending
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 rotate-90 md:rotate-0" data-id="element-2075" />
          <div className="flex flex-col gap-2" data-id="element-2076">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-2077">
              <span className="text-sm font-semibold text-green-900" data-id="element-2078">
                succeeded
              </span>
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-2079">
              <span className="text-sm font-semibold text-red-900" data-id="element-2080">failed</span>
            </div>
          </div>
        </div>
      </div>

      <h2 id="endpoints" data-id="element-2081">Endpoint Index</h2>

      <h3 data-id="element-2082">Wallets</h3>
      <ul className="space-y-2 my-4" data-id="element-2083">
        <li data-id="element-2084">
          <code className="text-sm" data-id="element-2085">POST /v1/wallets</code> - Create wallet
        </li>
        <li data-id="element-2086">
          <code className="text-sm" data-id="element-2087">GET /v1/wallets</code> - List wallets
        </li>
        <li data-id="element-2088">
          <code className="text-sm" data-id="element-2089">GET /v1/wallets/:id</code> - Retrieve wallet
        </li>
        <li data-id="element-2090">
          <code className="text-sm" data-id="element-2091">POST /v1/wallets/:id/freeze</code> - Freeze
          wallet
        </li>
        <li data-id="element-2092">
          <code className="text-sm" data-id="element-2093">POST /v1/wallets/:id/unfreeze</code> -
          Unfreeze wallet
        </li>
      </ul>

      <h3 data-id="element-2094">Balances</h3>
      <ul className="space-y-2 my-4" data-id="element-2095">
        <li data-id="element-2096">
          <code className="text-sm" data-id="element-2097">GET /v1/wallets/:id/balances</code> - Get
          wallet balances
        </li>
      </ul>

      <h3 data-id="element-2098">Transfers</h3>
      <ul className="space-y-2 my-4" data-id="element-2099">
        <li data-id="element-2100">
          <code className="text-sm" data-id="element-2101">POST /v1/wallets/transfers</code> - Create
          transfer
        </li>
        <li data-id="element-2102">
          <code className="text-sm" data-id="element-2103">GET /v1/wallets/transfers/:id</code> -
          Retrieve transfer
        </li>
      </ul>

      <h3 data-id="element-2104">Ledger</h3>
      <ul className="space-y-2 my-4" data-id="element-2105">
        <li data-id="element-2106">
          <code className="text-sm" data-id="element-2107">GET /v1/wallets/:id/ledger-entries</code> -
          List ledger entries
        </li>
      </ul>

      <h2 id="create-transfer" data-id="element-2108">Create Transfer</h2>
      <p data-id="element-2109">Transfer funds between wallets.</p>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-2110">
        <code className="text-sm font-semibold" data-id="element-2111">
          POST /v1/wallets/transfers
        </code>
      </div>

      <h3 data-id="element-2112">Parameters</h3>
      <table className="w-full text-sm my-4" data-id="element-2113">
        <thead data-id="element-2114">
          <tr className="border-b border-slate-200" data-id="element-2115">
            <th className="text-left py-2 pr-4" data-id="element-2116">Parameter</th>
            <th className="text-left py-2 pr-4" data-id="element-2117">Type</th>
            <th className="text-left py-2 pr-4" data-id="element-2118">Required</th>
            <th className="text-left py-2" data-id="element-2119">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-2120">
          <tr className="border-b border-slate-100" data-id="element-2121">
            <td className="py-2 pr-4" data-id="element-2122">
              <code data-id="element-2123">source_wallet_id</code>
            </td>
            <td className="py-2 pr-4" data-id="element-2124">string</td>
            <td className="py-2 pr-4" data-id="element-2125">Yes</td>
            <td className="py-2" data-id="element-2126">Source wallet ID</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2127">
            <td className="py-2 pr-4" data-id="element-2128">
              <code data-id="element-2129">destination_wallet_id</code>
            </td>
            <td className="py-2 pr-4" data-id="element-2130">string</td>
            <td className="py-2 pr-4" data-id="element-2131">Yes</td>
            <td className="py-2" data-id="element-2132">Destination wallet ID</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2133">
            <td className="py-2 pr-4" data-id="element-2134">
              <code data-id="element-2135">amount</code>
            </td>
            <td className="py-2 pr-4" data-id="element-2136">integer</td>
            <td className="py-2 pr-4" data-id="element-2137">Yes</td>
            <td className="py-2" data-id="element-2138">Amount in minor units</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2139">
            <td className="py-2 pr-4" data-id="element-2140">
              <code data-id="element-2141">currency</code>
            </td>
            <td className="py-2 pr-4" data-id="element-2142">string</td>
            <td className="py-2 pr-4" data-id="element-2143">Yes</td>
            <td className="py-2" data-id="element-2144">Three-letter ISO currency code</td>
          </tr>
        </tbody>
      </table>

      <h3 data-id="element-2145">Example Request</h3>
      <CodeBlock code={createTransferCode} data-id="element-2146" />

      <h3 data-id="element-2147">Example Response</h3>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-2148">
        <code data-id="element-2149">{`{
  "id": "tr_01J9Z98K5K8C0MZ9QK3G4B1E2P",
  "object": "wallets.transfer",
  "status": "succeeded",
  "source_wallet_id": "wa_123",
  "destination_wallet_id": "wa_456",
  "amount": 100000,
  "currency": "USD",
  "created_at": "2026-01-03T18:41:22Z"
}`}</code>
      </pre>

      <h2 id="webhooks" data-id="element-2150">Webhook Events</h2>
      <ul className="space-y-2 my-4" data-id="element-2151">
        <li data-id="element-2152">
          <code data-id="element-2153">wallets.wallet.created</code> - Wallet created
        </li>
        <li data-id="element-2154">
          <code data-id="element-2155">wallets.balance.updated</code> - Balance changed
        </li>
        <li data-id="element-2156">
          <code data-id="element-2157">wallets.transfer.created</code> - Transfer initiated
        </li>
        <li data-id="element-2158">
          <code data-id="element-2159">wallets.transfer.succeeded</code> - Transfer completed
        </li>
        <li data-id="element-2160">
          <code data-id="element-2161">wallets.transfer.failed</code> - Transfer failed
        </li>
        <li data-id="element-2162">
          <code data-id="element-2163">wallets.ledger_entry.created</code> - New ledger entry
        </li>
      </ul>

      <h2 id="errors" data-id="element-2164">Common Errors</h2>
      <ul className="space-y-2 my-4" data-id="element-2165">
        <li data-id="element-2166">
          <code data-id="element-2167">insufficient_balance</code> - Not enough funds in source wallet
        </li>
        <li data-id="element-2168">
          <code data-id="element-2169">wallet_frozen</code> - Wallet is frozen
        </li>
        <li data-id="element-2170">
          <code data-id="element-2171">currency_mismatch</code> - Currency not supported by wallet
        </li>
      </ul>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-2172">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-2173">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2174">
          <a href="#/docs/guides/idempotency" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-2175">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-2176">Idempotency</h4>
            <p className="text-sm text-slate-600" data-id="element-2177">Safe transfer retries</p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-2178">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-2179">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-2180">Complete specifications</p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}